import { makeAutoObservable, runInAction } from "mobx";
import RootStore from "stores/RootStore";
import ServiceStore from "stores/ServiceStore";
import type { materials, materialsData } from "types/api-types/manufacturing/materials/materials.types";
import type {
  materialInventory,
  materialInventoryData
} from "types/api-types/manufacturing/material-inventory/material-inventory.types";

interface VendorInfo {
  vendorId: string;
  vendorPartNumber: string;
  unitPrice: number;
  minOrderQuantity: number;
  leadTimeDays: number;
  isPrimary: boolean;
}

interface FormData {
  // Basic Info (matching materials type)
  materialCode: string;
  name: string;
  description: string;
  category: string;
  unit: string;
  subcategory: string;
  materialType: string;

  // Specifications
  minOrderQuantity: number;
  orderMultiple: number;
  shelfLife: number | null;
  hazardous: boolean;
  density: number | null;
  specifications: string;
  msdsUrl: string;

  // Vendors
  vendors: VendorInfo[];

  // Initial Stock
  initialQuantity: number;
  locationId: string;
  lotNumber: string;
  expirationDate: string;
  unitCost: number;
  notes: string;
}

class AddRawMaterialsWizardStore {
  rootStore: RootStore;

  // UI State
  activeStep = 0;
  loading = false;
  error: string | null = null;
  success: string | null = null;

  // Material Selection State
  isNewMaterial = false; // Toggle between new and existing material
  selectedExistingMaterial: materials | null = null;
  materialInventoryByLocation: Map<number, materialInventory[]> = new Map(); // locationId -> inventory records
  selectedBatchMode: "new" | "existing" = "new"; // For existing materials
  selectedExistingBatch: materialInventory | null = null;

  // Form Data
  formData: FormData = {
    materialCode: "",
    name: "",
    description: "",
    category: "raw-material",
    unit: "EA",
    subcategory: "",
    materialType: "raw-material",
    minOrderQuantity: 0,
    orderMultiple: 1,
    shelfLife: null,
    hazardous: false,
    density: null,
    specifications: "",
    msdsUrl: "",
    vendors: [],
    initialQuantity: 0,
    locationId: "",
    lotNumber: "",
    expirationDate: "",
    unitCost: 0,
    notes: "",
  };

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  // Actions
  setActiveStep = (step: number) => {
    this.activeStep = step;
  };

  nextStep = () => {
    // Skip specifications step for existing materials
    if (!this.isNewMaterial && this.activeStep === 0) {
      this.activeStep = 2; // Skip to vendors
    } else {
      this.activeStep++;
    }
  };

  prevStep = () => {
    // Skip specifications step for existing materials when going back
    if (!this.isNewMaterial && this.activeStep === 2) {
      this.activeStep = 0; // Skip back to material selection
    } else {
      this.activeStep--;
    }
  };

  setError = (error: string | null) => {
    this.error = error;
  };

  setSuccess = (success: string | null) => {
    this.success = success;
  };

  setIsNewMaterial = (isNew: boolean) => {
    this.isNewMaterial = isNew;
    // Reset selected material when switching modes
    if (isNew) {
      this.selectedExistingMaterial = null;
      this.materialInventoryByLocation.clear();
      this.selectedBatchMode = "new";
      this.selectedExistingBatch = null;
    } else {
      // Clear new material fields
      this.formData.materialCode = "";
      this.formData.name = "";
      this.formData.description = "";
      this.formData.category = "raw-material";
      this.formData.unit = "EA";
      this.formData.subcategory = "";
      this.formData.materialType = "raw-material";
    }
  };

  setBatchMode = (mode: "new" | "existing") => {
    this.selectedBatchMode = mode;
    if (mode === "new") {
      this.selectedExistingBatch = null;
      // Clear batch-specific fields for new batch
      this.formData.lotNumber = "";
      this.formData.expirationDate = "";
    }
  };

  selectExistingBatch = (batch: materialInventory | null) => {
    this.selectedExistingBatch = batch;
    if (batch) {
      // Pre-fill form with existing batch data
      this.formData.locationId = batch.locationId.toString();
      this.formData.lotNumber = batch.lotNumber || "";
      this.formData.expirationDate = batch.expirationDate || "";
      this.formData.unitCost = batch.cost || 0;
    }
  };

  selectExistingMaterial = async (material: materials | null) => {
    this.selectedExistingMaterial = material;
    this.materialInventoryByLocation.clear();
    this.selectedBatchMode = "new";
    this.selectedExistingBatch = null;

    if (material) {
      // Load current inventory information for this material
      await this.loadMaterialInventory(material.id);

      // Pre-fill form data from existing material
      this.formData.materialCode = material.materialCode;
      this.formData.name = material.name;
      this.formData.description = material.description || "";
      this.formData.category = material.category || "raw-material";
      this.formData.unit = material.unit || "EA";
      this.formData.subcategory = material.subcategory || "";
      this.formData.materialType = material.materialType || "raw-material";

      // Use existing specifications if available
      this.formData.minOrderQuantity = material.minOrderQuantity || 0;
      this.formData.orderMultiple = material.orderMultiple || 1;
      this.formData.shelfLife = material.shelfLife || null;
      this.formData.hazardous = material.hazardous || false;
      this.formData.density = material.density || null;
      this.formData.specifications = material.specifications || "";
      this.formData.msdsUrl = material.msdsUrl || "";
    }
  };

  updateFormData = <K extends keyof FormData>(field: K, value: FormData[K]) => {
    this.formData[field] = value;
  };

  addVendor = () => {
    const newVendor: VendorInfo = {
      vendorId: "",
      vendorPartNumber: "",
      unitPrice: 0,
      minOrderQuantity: 1,
      leadTimeDays: 0,
      isPrimary: this.formData.vendors.length === 0, // First vendor is primary by default
    };
    this.formData.vendors.push(newVendor);
  };

  updateVendor = (index: number, vendor: VendorInfo) => {
    // If setting as primary, unset other primary vendors
    if (vendor.isPrimary) {
      this.formData.vendors.forEach((v, i) => {
        if (i !== index) v.isPrimary = false;
      });
    }
    this.formData.vendors[index] = vendor;
  };

  removeVendor = (index: number) => {
    this.formData.vendors.splice(index, 1);
  };

  // Async Actions
  loadInitialData = async () => {
    this.loading = true;
    this.error = null;

    try {
      const materialsStore = this.rootStore.materialsStore as ServiceStore;
      const locationsStore = this.rootStore.materialLocationsStore as ServiceStore;
      const vendorsStore = this.rootStore.materialVendorsStore as ServiceStore;
      const inventoryStore = this.rootStore.materialInventoryStore as ServiceStore;

      await Promise.all([
        materialsStore?.find(),
        locationsStore?.find(),
        vendorsStore?.find(),
        inventoryStore?.find(), // Load material-inventory records
      ]);

      runInAction(() => {
        this.loading = false;
      });
    } catch (err: any) {
      runInAction(() => {
        this.error = err.message || "Failed to load data";
        this.loading = false;
      });
    }
  };

  loadMaterialInventory = async (materialId: number) => {
    try {
      const inventoryStore = this.rootStore.materialInventoryStore as ServiceStore<materialInventory>;

      // Find all inventory records for this material
      const inventoryRecords = inventoryStore?.entities.filter(
        (inv: materialInventory) => inv.materialId === materialId
      ) || [];

      // Group by location
      const byLocation = new Map<number, materialInventory[]>();
      inventoryRecords.forEach((inv: materialInventory) => {
        const locationRecords = byLocation.get(inv.locationId) || [];
        locationRecords.push(inv);
        byLocation.set(inv.locationId, locationRecords);
      });

      runInAction(() => {
        this.materialInventoryByLocation = byLocation;
      });
    } catch (err) {
      console.error("Failed to load material inventory:", err);
      runInAction(() => {
        this.materialInventoryByLocation.clear();
      });
    }
  };

  submitMaterial = async () => {
    this.loading = true;
    this.error = null;

    try {
      const materialsStore = this.rootStore.materialsStore as ServiceStore;
      const inventoryStore = this.rootStore.materialInventoryStore as ServiceStore;
      const vendorsStore = this.rootStore.materialVendorsStore as ServiceStore;

      let materialId: string;
      let materialName: string;

      if (this.isNewMaterial) {
        // Create new material using correct types
        const materialData: materialsData = {
          materialCode: this.formData.materialCode,
          name: this.formData.name,
          description: this.formData.description,
          category: this.formData.category,
          subcategory: this.formData.subcategory,
          materialType: this.formData.materialType,
          unit: this.formData.unit,
          minOrderQuantity: this.formData.minOrderQuantity,
          orderMultiple: this.formData.orderMultiple,
          shelfLife: this.formData.shelfLife || undefined,
          hazardous: this.formData.hazardous,
          density: this.formData.density || undefined,
          specifications: this.formData.specifications,
          msdsUrl: this.formData.msdsUrl,
          active: true,
        };

        const newMaterial = await materialsStore?.create(materialData);

        if (!newMaterial) {
          throw new Error("Failed to create material");
        }

        materialId = newMaterial.id.toString();
        materialName = this.formData.name;
      } else {
        // Use existing material
        if (!this.selectedExistingMaterial) {
          throw new Error("No material selected");
        }

        materialId = this.selectedExistingMaterial.id.toString();
        materialName = this.selectedExistingMaterial.name;
      }

      // Add or update inventory record
      if (this.formData.initialQuantity > 0) {
        const materialIdNum = parseInt(materialId);
        const locationIdNum = parseInt(this.formData.locationId);

        if (!this.isNewMaterial && this.selectedBatchMode === "existing" && this.selectedExistingBatch) {
          // Update existing batch
          const updatedQuantity = this.selectedExistingBatch.quantity + this.formData.initialQuantity;
          const availableQuantity = updatedQuantity - (this.selectedExistingBatch.reservedQuantity || 0);

          await inventoryStore?.patch(this.selectedExistingBatch.id, {
            quantity: updatedQuantity,
            availableQuantity: availableQuantity,
            cost: this.formData.unitCost || this.selectedExistingBatch.cost,
            notes: this.formData.notes || this.selectedExistingBatch.notes,
          });
        } else {
          // Create new inventory record (new batch)
          const inventoryData: materialInventoryData = {
            materialId: materialIdNum,
            locationId: locationIdNum,
            quantity: this.formData.initialQuantity,
            reservedQuantity: 0, // New stock has no reservations
            lotNumber: this.formData.lotNumber || undefined,
            batchNumber: this.formData.lotNumber || undefined, // Using lot number as batch
            receivedDate: new Date().toISOString(), // Set current date as received date
            expirationDate: this.formData.expirationDate || undefined,
            cost: this.formData.unitCost || undefined,
            vendorId: this.formData.vendors.length > 0 ?
              parseInt(this.formData.vendors.find(v => v.isPrimary)?.vendorId || this.formData.vendors[0].vendorId) :
              undefined,
            status: "available",
            notes: this.formData.notes || undefined,
          };

          await inventoryStore?.create(inventoryData);
        }
      }

      // Create/update vendor relationships
      for (const vendor of this.formData.vendors) {
        if (vendor.vendorId) {
          // Check if vendor relationship exists
          const existingVendor = vendorsStore?.entities.find(
            (v: any) => v.materialId === materialId && v.vendorId === vendor.vendorId
          );

          if (existingVendor) {
            // Update existing vendor relationship
            await vendorsStore?.patch(existingVendor.id, {
              vendorPartNumber: vendor.vendorPartNumber,
              unitPrice: vendor.unitPrice,
              minimumOrderQuantity: vendor.minOrderQuantity,
              leadTimeDays: vendor.leadTimeDays,
              isPrimaryVendor: vendor.isPrimary,
            });
          } else {
            // Create new vendor relationship
            await vendorsStore?.create({
              materialId: materialId,
              vendorId: vendor.vendorId,
              vendorPartNumber: vendor.vendorPartNumber,
              unitPrice: vendor.unitPrice,
              minimumOrderQuantity: vendor.minOrderQuantity,
              leadTimeDays: vendor.leadTimeDays,
              isPrimaryVendor: vendor.isPrimary,
            });
          }
        }
      }

      runInAction(() => {
        const action = this.isNewMaterial ? "created" : "updated";
        this.success = `Material "${materialName}" has been ${action} successfully! ${
          this.formData.initialQuantity > 0
            ? `Added ${this.formData.initialQuantity} ${this.formData.unit} to inventory.`
            : ""
        }`;
        this.loading = false;
        this.nextStep();
      });
    } catch (err: any) {
      runInAction(() => {
        this.error = err.message || "Failed to process material";
        this.loading = false;
      });
    }
  };

  reset = () => {
    this.activeStep = 0;
    this.loading = false;
    this.error = null;
    this.success = null;
    this.isNewMaterial = false;
    this.selectedExistingMaterial = null;
    this.currentInventoryInfo = null;
    this.formData = {
      materialCode: "",
      name: "",
      description: "",
      category: "raw-material",
      unit: "EA",
      subcategory: "",
      materialType: "raw-material",
      minOrderQuantity: 0,
      orderMultiple: 1,
      shelfLife: null,
      hazardous: false,
      density: null,
      specifications: "",
      msdsUrl: "",
      vendors: [],
      initialQuantity: 0,
      locationId: "",
      lotNumber: "",
      expirationDate: "",
      unitCost: 0,
      notes: "",
    };
  };

  // Computed Values
  get canProceedFromStep1() {
    if (this.isNewMaterial) {
      return (
        !!this.formData.materialCode &&
        !!this.formData.name &&
        !!this.formData.category &&
        !!this.formData.unit
      );
    } else {
      return !!this.selectedExistingMaterial;
    }
  }

  get canProceedFromStep4() {
    return (
      this.formData.initialQuantity >= 0 &&
      !!this.formData.locationId
    );
  }

  get locations() {
    const locationsStore = this.rootStore.materialLocationsStore as ServiceStore;
    return locationsStore?.entities || [];
  }

  get vendors() {
    const vendorsStore = this.rootStore.materialVendorsStore as ServiceStore;
    return vendorsStore?.entities || [];
  }

  get materials(): materials[] {
    const materialsStore = this.rootStore.materialsStore as ServiceStore<materials>;
    return materialsStore?.entities || [];
  }

  get materialInventory(): materialInventory[] {
    const inventoryStore = this.rootStore.materialInventoryStore as ServiceStore<materialInventory>;
    return inventoryStore?.entities || [];
  }

  get totalQuantityAcrossLocations(): number {
    let total = 0;
    this.materialInventoryByLocation.forEach((inventoryList) => {
      inventoryList.forEach((inv) => {
        total += inv.quantity || 0;
      });
    });
    return total;
  }

  get totalAvailableAcrossLocations(): number {
    let total = 0;
    this.materialInventoryByLocation.forEach((inventoryList) => {
      inventoryList.forEach((inv) => {
        total += inv.availableQuantity || (inv.quantity - (inv.reservedQuantity || 0));
      });
    });
    return total;
  }

  get inventoryLocationSummary(): Array<{
    locationId: number;
    locationName: string;
    batches: materialInventory[];
    totalQuantity: number;
    totalAvailable: number;
  }> {
    const summary: Array<{
      locationId: number;
      locationName: string;
      batches: materialInventory[];
      totalQuantity: number;
      totalAvailable: number;
    }> = [];

    this.materialInventoryByLocation.forEach((batches, locationId) => {
      const location = this.locations.find((loc: any) => loc.id === locationId);
      const totalQuantity = batches.reduce((sum, inv) => sum + (inv.quantity || 0), 0);
      const totalAvailable = batches.reduce(
        (sum, inv) => sum + (inv.availableQuantity || (inv.quantity - (inv.reservedQuantity || 0))),
        0
      );

      summary.push({
        locationId,
        locationName: location?.name || `Location ${locationId}`,
        batches,
        totalQuantity,
        totalAvailable,
      });
    });

    return summary;
  }
}

export default AddRawMaterialsWizardStore;